/**
 * Everything the open conversation can have done to it, as one row of circles.
 *
 * Three of these used to live on a strip below the header instead - link a
 * client, make a ticket, never write here again - each drawn as a wide labelled
 * pill. Three words cost a whole row of a pane whose only job is showing a
 * letter, and a reader looking for "what can I do with this?" had to check two
 * places that looked nothing alike. They are circles now, in the row that
 * already answered that question.
 *
 * The row wraps rather than overflowing. Eight controls do not fit next to a
 * subject line in a narrow pane, and a button pushed off the edge of a card
 * with `overflow: hidden` on it is a button that is simply gone.
 */
import { Archive, ArchiveRestore, Forward, Reply, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailThread } from "../../../../lib/mail";
import type { CrmLabel } from "../../../../lib/crmLabels";
import { IconBtn } from "../../../ui";
import { useWorkCompact } from "../../_shared/density";
import type { TemplateSeed } from "../TemplateMenu";
import { ClientLinkButton } from "./ClientLinkButton";
import { SuppressButton } from "./SuppressButton";
import { ThreadMenu } from "./ThreadMenu";
import { TicketButton } from "./TicketButton";

export function ThreadActions({
  thread,
  labels,
  onReply,
  onForward,
  onPatch,
  onDelete,
  onChanged,
}: {
  thread: MailThread;
  labels: CrmLabel[];
  onReply: (seed?: TemplateSeed) => void;
  onForward: () => void;
  onPatch: (body: Record<string, unknown>) => Promise<void>;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const compact = useWorkCompact();
  const archived = thread.status === "archived";
  const spam = thread.status === "spam";

  return (
    <div className={cn("crm-read-acts", compact ? "gap-1.5" : "gap-2")}>
      <IconBtn onClick={() => onReply()} title={t("compose.reply")} aria-label={t("compose.reply")}>
        <Reply className="size-4" />
      </IconBtn>
      <IconBtn onClick={onForward} title={t("compose.forward")} aria-label={t("compose.forward")}>
        <Forward className="size-4" />
      </IconBtn>

      {/* The filing group, between answering and disposing of the thread: these
          three say what the conversation IS, which is what an operator decides
          before deciding where to put it. */}
      <ClientLinkButton thread={thread} onPatch={onPatch} />
      <TicketButton thread={thread} onChanged={onChanged} />
      <SuppressButton thread={thread} />

      <IconBtn
        onClick={() => void onPatch({ status: archived ? "open" : "archived" })}
        title={t(archived ? "email.unarchive" : "email.archive")}
        aria-label={t(archived ? "email.unarchive" : "email.archive")}
      >
        {archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
      </IconBtn>
      <IconBtn onClick={onDelete} title={t("email.delete")} aria-label={t("email.delete")}>
        <Trash2 className="size-4" />
      </IconBtn>

      <ThreadMenu thread={thread} spam={spam} labels={labels} onPatch={onPatch} onDelete={onDelete} />
    </div>
  );
}
